/**
 * Public therapy entry points:
 *   /therapy             — landing/list of therapists
 *   /therapy/apply       — therapist application form
 *   /therapy/book/:slug  — book a slot with a therapist
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart, Star, Calendar, Clock, ChevronRight, ShieldCheck, Sparkles, ArrowLeft,
} from "lucide-react";
import Seo from "../components/Seo";
import { useAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtMin(m) {
  const h = Math.floor(m / 60), mm = m % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

/* ------------- /therapy landing ------------- */
export function TherapyHomePage() {
  const [therapists, setTherapists] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    axios.get(`${API}/therapy/therapists`).then((r) => setTherapists(r.data || []));
    axios.get(`${API}/therapy/settings`).then((r) => setSettings(r.data || {}));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="therapy-home-page">
      <Seo title="Therapy — LockedInForever" path="/therapy" />
      <div className="container-wide max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-14">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3">Couples Therapy · Premarital · Relationship</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl mb-4">Talk. Heal. Lock in.</h1>
          <p className="text-black/60 text-lg max-w-2xl mx-auto">
            Private 1:1 video sessions with vetted therapists. Encrypted, branded, completely confidential.
          </p>
          {settings.thumbnail_url && (
            <img src={settings.thumbnail_url.startsWith("http") ? settings.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${settings.thumbnail_url}`}
              alt="" className="mx-auto mt-10 rounded-2xl max-w-md w-full object-cover shadow-xl" />
          )}
        </div>

        <div className="flex justify-end mb-6">
          <Link to="/therapy/apply" className="text-sm text-[var(--gold)] underline" data-testid="therapy-apply-link">Are you a therapist? Apply →</Link>
        </div>

        {therapists.length === 0 ? (
          <div className="text-center py-12 text-sm text-black/40">No therapists onboarded yet. Check back soon.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="therapy-list">
            {therapists.map((t) => (
              <Link key={t.user_id} to={`/therapy/book/${t.slug}`}
                className="bg-white border border-[var(--line)] rounded-2xl p-6 hover:shadow-xl hover:border-[#C8A96B]/40 transition-all"
                data-testid={`therapist-card-${t.slug}`}>
                {t.photo_url && (
                  <img src={t.photo_url} alt="" className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-[#C8A96B]/30" />
                )}
                <h3 className="font-serif text-2xl">{t.name}</h3>
                <p className="text-xs text-black/40 mt-1">{(t.specialties || []).slice(0, 3).join(" · ") || "Couples Therapy"}</p>
                <p className="text-sm text-black/70 mt-3 line-clamp-3">{t.bio}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--line)]">
                  <span className="text-sm font-medium">${t.hourly_rate}/hour</span>
                  <span className="text-[var(--gold)] text-sm flex items-center gap-1">Book <ChevronRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------- /therapy/apply ------------- */
export function TherapyApplyPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    license_number: "", license_state: "", specialties: "", bio: "",
    photo_url: "", hourly_rate: 150,
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-sm text-black/60 mb-4">Please log in or create an account first.</p>
          <Link to="/login?next=/therapy/apply" className="btn-gold">Log in</Link>
        </div>
      </div>
    );
  }

  async function submit() {
    if (!form.license_number || !form.license_state || form.bio.length < 20) {
      return toast.error("License, state, and a 20+ char bio are required.");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/therapy/apply`, {
        ...form,
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
        hourly_rate: Number(form.hourly_rate),
      }, { withCredentials: true });
      setSubmitted(true);
      toast.success("Application submitted! Admin will review.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="therapy-apply-page">
      <Seo title="Apply as a Therapist" path="/therapy/apply" />
      <div className="container-wide max-w-2xl mx-auto px-6 py-12">
        <Link to="/therapy" className="text-sm text-black/50 flex items-center gap-1 mb-6"><ArrowLeft className="w-3 h-3" /> Back</Link>
        <h1 className="font-serif text-4xl mb-2">Therapist Application</h1>
        <p className="text-sm text-black/60 mb-8">Reviewed manually by our team. We respond within 2 business days.</p>

        {submitted ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="therapy-apply-done">
            <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h2 className="font-serif text-2xl">Application received</h2>
            <p className="text-sm text-black/60 mt-2">We'll email you when it's reviewed.</p>
          </div>
        ) : (
          <div className="bg-white border border-[var(--line)] rounded-2xl p-8 space-y-4">
            <Field label="License number" v={form.license_number} on={(v) => setForm({ ...form, license_number: v })} testid="apply-license" />
            <Field label="License state / jurisdiction" v={form.license_state} on={(v) => setForm({ ...form, license_state: v })} testid="apply-state" />
            <Field label="Specialties (comma-separated)" v={form.specialties} on={(v) => setForm({ ...form, specialties: v })} placeholder="Couples therapy, Premarital, Anxiety" testid="apply-specialties" />
            <Field label="Hourly rate (USD)" v={form.hourly_rate} on={(v) => setForm({ ...form, hourly_rate: v })} type="number" testid="apply-rate" />
            <Field label="Photo URL (optional)" v={form.photo_url} on={(v) => setForm({ ...form, photo_url: v })} placeholder="https://..." testid="apply-photo" />
            <div>
              <label className="text-[11px] uppercase tracking-widest text-black/50">Bio (min 20 chars)</label>
              <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={6}
                className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
                data-testid="apply-bio" />
            </div>
            <button onClick={submit} disabled={busy} className="btn-gold w-full" data-testid="apply-submit">
              {busy ? "Submitting…" : "Submit application"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", testid, placeholder = "" }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-black/50">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} type={type} placeholder={placeholder}
        className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
        data-testid={testid} />
    </div>
  );
}

/* ------------- /therapy/book/:slug ------------- */
export function TherapyBookPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(50);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/therapy/therapists/${slug}`).then((r) => setT(r.data))
      .catch(() => setT(false));
  }, [slug]);

  async function book() {
    if (!user) return nav(`/login?next=/therapy/book/${slug}`);
    if (!date || !time) return toast.error("Pick a date and time.");
    const startsAt = new Date(`${date}T${time}:00Z`).toISOString();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/therapy/book`, {
        therapist_user_id: t.user_id,
        starts_at: startsAt,
        duration_minutes: Number(duration),
      }, { withCredentials: true });
      toast.success("Session booked!");
      nav(`/therapy/intake/${r.data.session_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  }

  if (t === false) return <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-black/60" data-testid="therapist-not-found">Therapist not found.<a href="/therapy" className="underline">See all therapists</a></div>;
  if (!t) return <div className="min-h-screen flex items-center justify-center text-sm text-black/40">Loading…</div>;
  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="therapy-book-page">
      <Seo title={`Book ${t.name}`} path={`/therapy/book/${slug}`} />
      <div className="container-wide max-w-3xl mx-auto px-6 py-12">
        <Link to="/therapy" className="text-sm text-black/50 flex items-center gap-1 mb-6"><ArrowLeft className="w-3 h-3" /> Back</Link>
        <div className="bg-white border border-[var(--line)] rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            {t.photo_url && <img src={t.photo_url} alt="" className="w-16 h-16 rounded-full object-cover" />}
            <div>
              <h1 className="font-serif text-3xl">{t.name}</h1>
              <p className="text-xs text-black/50">${t.hourly_rate}/hr · {(t.specialties || []).join(" · ")}</p>
            </div>
          </div>
          <p className="text-sm text-black/70 mb-6">{t.bio}</p>

          {(t.availability || []).length > 0 && (
            <div className="bg-black/5 rounded-xl p-4 mb-6">
              <p className="text-[11px] uppercase tracking-widest text-black/50 mb-2">Weekly availability (UTC)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {t.availability.map((a) => (
                  <div key={a.slot_id} className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-[var(--gold)]" />
                    {WEEKDAYS[a.weekday]} · {fmtMin(a.start_minutes)} – {fmtMin(a.end_minutes)}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-black/50">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="book-date" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-black/50">Time (UTC)</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="book-time" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-black/50">Duration</label>
              <select value={duration} onChange={(e) => setDuration(e.target.value)}
                className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="book-duration">
                <option value={30}>30 min</option>
                <option value={50}>50 min</option>
                <option value={90}>90 min</option>
              </select>
            </div>
          </div>
          <button onClick={book} disabled={busy} className="btn-gold w-full" data-testid="book-submit">
            <Calendar className="w-4 h-4" /> Book session — ${(t.hourly_rate * (Number(duration) / 60)).toFixed(2)}
          </button>
          <p className="text-[11px] text-black/40 mt-3 text-center">You'll complete an intake form and pay on the next screen.</p>
        </div>
      </div>
    </div>
  );
}
